import { useEffect, useState } from "react";

// @mui material components
import Container from "@mui/material/Container";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import Stack from "@mui/material/Stack";

// Material Kit 2 PRO React components
import MKBox from "components/MKBox";
import MKTypography from "components/MKTypography";
import MKButton from "components/MKButton";

// Material Kit 2 PRO React examples
import Navbar from "components/Navbar";
import AboutMeHeader from "./AboutMeHeader";
import Footer from "components/Footer";
import ExperienceCard from "./ExperienceCard";

// Coworking page sections

// Routes
import routes from "routes";
import footerRoutes from "footer.routes";

// Images
import JCaglePhoto from "assets/photos/JCagle.JPG";
import EducationCollapse from "./EducationCollapse";

const publications = [
  { 
    title: "Finely tuned gamma oscillations in the globus pallidus internus in Parkinson's disease.",
    authors: ["Kamo, H.", "Eisel, M.L.S.", "Cagle, J.N.", "Johnson, K.A.", "Wong, J.", "de Hemptinne, C."],
    journal: "Movement Disorders Clinical Practice",
    year: 2025,
    doi: "https://doi.org/10.1002/mdc3.70251"
  },
  {
    title: "Chronic intracranial recordings in the globus pallidus reveal circadian rhythms in Parkinson’s disease.",
    authors: ["Cagle, J.N.", "de Araujo, T.", "Johnson, K.A.", "Yu, J.", "Fanty, L.", "Sarmento, F.P.", "et al."],
    journal: "Nature Communications",
    year: 2024,
    doi: "10.1038/s41467-024-48732-0"
  },
  {
    title: "Responsive deep brain stimulation for the treatment of Tourette syndrome.",
    authors: ["Okun, M.S.", "Cagle, J.N.", "Gomez, J.", "Bowers, D.", "Wong, J.", "Foote, K.D.", "Gunduz, A."],
    journal: "Scientific Reports",
    year: 2024,
    doi: "10.1038/s41598-024-57071-5"
  },
  {
    title: "Weight and survival after deep brain stimulation for Parkinson's disease.",
    authors: ["Eisinger, R.S.", "Okun, M.S.", "Cernera, S.", "Cagle, J.N.", "Beke, M.", "Ramirez-Zamora, A.", "et al."],
    journal: "Parkinsonism & Related Disorders",
    year: 2023,
    doi: "10.1016/j.parkreldis.2023.105810"
  }, 
  { 
    title: "Brain Recording Analysis and Visualization Online (BRAVO): An open-source visualization tool for deep brain stimulation data.",
    authors: ["Cagle, J.N.", "Johnson, K.A.", "Almeida, L.", "Wong, J.K.", "Ramirez-Zamora, A.", "Okun, M.S.", "et al."],
    journal: "Brain Stimulation",
    year: 2023,
    doi: "10.1016/j.brs.2023.04.018"
  }, 
  {
    title: "Globus pallidus internus deep brain stimulation evokes resonant neural activity in Parkinson’s disease.",
    authors: ["Johnson, K.A.", "Cagle, J.N.", "Lopes, J.L.", "Wong, J.K.", "Okun, M.S.", "Gunduz, A.", "et al."],
    journal: "Brain Communications",
    year: 2023,
    doi: "10.1093/braincomms/fcad025"
  },
  {
    title: "Phenomenology and physiology of tacrolimus induced tremor.",
    authors: ["Shukla, A.W.", "Lunny, C.", "Hisham, I.", "Cagle, J.N.", "Malea, J.", "Santos, A.", "Shukla, A.M."],
    journal: "Tremor and Other Hyperkinetic Movements",
    year: 2023,
    doi: "10.5334/tohm.725"
  },
  {
    title: "Tachygastria in preterm infants: A longitudinal cohort study.",
    authors: ["Ortigoza, E.B.", "Cagle, J.N.", "Brown, L.S.", "Mansi, S.", "Gosser, S.P.", "Montgomery, A.D.", "et al."],
    journal: "Journal of Pediatric Gastroenterology and Nutrition",
    year: 2022,
    doi: "10.1097/mpg.0000000000003575"
  },
  {
    title: "Embedded human closed-loop deep brain stimulation for tourette syndrome: a nonrandomized controlled trial.",
    authors: ["Cagle, J. N.", "Okun, M. S.", "Cernera, S.", "Eisinger, R. S.", "Opri, E.", "Bowers, D.", "...", "Gunduz, A."],
    journal: "JAMA neurology",
    year: 2022,
    doi: "10.1001/jamaneurol.2022.2741"
  },
  {
    title: "Past, present, and future of deep brain stimulation: hardware, software, imaging, physiology and novel approaches.",
    authors: ["Frey, J.", "Cagle, J.", "Johnson, K. A.", "Wong, J. K.", "Hilliard, J. D.", "Butson, C. R.", "...", "de Hemptinne, C."],
    journal: "Frontiers in neurology",
    year: 2022,
    doi: "10.3389/fneur.2022.825178"
  },
  {
    title: "Distinct roles of the human subthalamic nucleus and dorsal pallidum in Parkinson’s disease impulsivity.",
    authors: ["Eisinger, R. S.", "Cagle, J. N.", "Alcantara, J. D.", "Opri, E.", "Cernera, S.", "Le, A.", "...", "Gunduz, A."],
    journal: "Biological psychiatry",
    year: 2022,
    doi: "10.1016/j.biopsych.2021.03.002"
  },
  {
    title: "The Human Tic Detector: An automatic approach to tic characterization using wearable sensors.",
    authors: ["Cernera, S.", "Pramanik, L.", "Boogaart, Z.", "Cagle, J. N.", "Gomez, J.", "Moore, K.", "...", "Deeb, W."],
    journal: "Clinical neurophysiology",
    year: 2022,
    doi: "10.1016/j.clinph.2021.10.017"
  },
  {
    title: "Practical closed-loop strategies for deep brain stimulation: lessons from chronic pain.",
    authors: ["Prosky, J.", "Cagle, J.", "Sellers, K. K.", "Gilron, R. E.", "de Hemptinne, C.", "Schmitgen, A.", "...", "Shirvalkar, P."],
    journal: "Frontiers in neuroscience",
    year: 2021,
    doi: "10.3389/fnins.2021.762097"
  },
  {
    title: "Wearable sensor-driven responsive deep brain stimulation for essential tremor.",
    authors: ["Cernera, S.", "Alcantara, J. D.", "Opri, E.", "Cagle, J. N.", "Eisinger, R. S.", "Boogaart, Z.", "...", "Gunduz, A."],
    journal: "Brain Stimulation",
    year: 2021,
    doi: "10.1016/j.brs.2021.09.002"
  },
  {
    title: "Suppression and rebound of pallidal beta power: observation using a chronic sensing DBS device.",
    authors: ["Cagle, J. N.", "Wong, J. K.", "Johnson, K. A.", "Foote, K. D.", "Okun, M. S.", "de Hemptinne, C."],
    journal: "Frontiers in Human Neuroscience",
    year: 2021,
    doi: "10.3389/fnhum.2021.749567"
  },
  {
    title: "A novel local field potential-based functional approach for targeting the centromedian-parafascicular complex for deep brain stimulation.",
    authors: ["Cagle, J. N.", "Eisinger, R. S.", "Holland, M. T.", "Foote, K. D.", "Okun, M. S.", "Gunduz, A."],
    journal: "NeuroImage: Clinical",
    year: 2021,
    doi: "10.1016/j.nicl.2021.102644"
  },
  {
    title: "Chronic embedded cortico-thalamic closed-loop deep brain stimulation for the treatment of essential tremor.",
    authors: ["Opri, E.", "Cernera, S.", "Molina, R.", "Eisinger, R. S.", "Cagle, J. N.", "Almeida, L.", "...", "Gunduz, A."],
    journal: "Brain Stimulation",
    year: 2021,
    doi: "10.1126/scitranslmed.aay7680"
  },
  {
    title: "Florida research open-source synchronization tool (FROST) for electrophysiology experiments.",
    authors: ["Alcantara, J. D.", "Eisinger, R. S.", "Opri, E.", "Kelberman, M.", "Cagle, J. N.", "Gomez, J.", "...", "Gunduz, A."],
    journal: "Journal of Neuroscience Methods",
    year: 2020,
    doi: "10.1016/j.jneumeth.2020.108800"
  },
  {
    title: "Lead repositioning guided by both physiology and atlas based targeting in Tourette deep brain stimulation.",
    authors: ["Cagle, J. N.", "Deeb, W.", "Eisinger, R. S.", "Molina, R.", "Opri, E.", "Holland, M. T.", "...", "Gunduz, A."],
    journal: "Tremor and Other Hyperkinetic Movements",
    year: 2020,
    doi: "10.5334/tohm.140"
  },
  {
    title: "Differentiating tic electrophysiology from voluntary movement in the human thalamocortical circuit.",
    authors: ["Cagle, J. N.", "Okun, M. S.", "Opri, E.", "Cernera, S.", "Molina, R.", "Foote, K. D.", "...", "Gunduz, A."],
    journal: "Journal of Neurology, Neurosurgery & Psychiatry",
    year: 2020,
    doi: "10.1136/jnnp-2019-321973"
  },
  {
    title: "Parkinsonian beta dynamics during rest and movement in the dorsal pallidum and subthalamic nucleus.",
    authors: ["Eisinger, R. S.", "Cagle, J. N.", "Opri, E.", "Alcantara, J.", "Cernera, S.", "Foote, K. D.", "...", "Gunduz, A."],
    journal: "Journal of Neuroscience",
    year: 2020,
    doi: "10.1523/jneurosci.2113-19.2020"
  },
  {
    title: "Proceedings of the seventh annual deep brain stimulation think tank: advances in neurophysiology, adaptive DBS, virtual reality, neuroethics and technology.",
    authors: ["Ramirez-Zamora, A.", "Giordano, J.", "Gunduz, A.", "Alcantara, J.", "Cagle, J. N.", "Cernera, S.", "...", "Okun, M. S."],
    journal: "Frontiers in Human Neuroscience",
    year: 2020,
    doi: "10.3389/fnhum.2020.00054"
  },
  {
    title: "Gait worsening and the microlesion effect following deep brain stimulation for essential tremor.",
    authors: ["Roemmich, R.", "Roper, J. A.", "Eisinger, R. S.", "Cagle, J. N.", "Maine, L.", "Deeb, W.", "...", "Hass, C. J."],
    journal: "Journal of Neurology, Neurosurgery & Psychiatry",
    year: 2019,
    doi: "10.1136/jnnp-2018-319723"
  },
  {
    title: "Task-based cognitive fatigability for older adults and validation of mental fatigability subscore of Pittsburgh Fatigability Scale.",
    authors: ["Burke, S. E.", "Babu Henry Samuel, I.", "Zhao, Q.", "Cagle, J.", "Cohen, R. A.", "Kluger, B.", "...", "Ding, M."],
    journal: "Frontiers in Aging Neuroscience",
    year: 2018,
    doi: "10.3389/fnagi.2018.00327"
  },
  {
    title: "Electrogastrography, near‐infrared spectroscopy, and acoustics to measure gastrointestinal development in preterm babies.",
    authors: ["Ortigoza, E. B.", "Cagle, J.", "Chien, J. H.", "Oh, S.", "Brown, L. S.", "Neu, J."],
    journal: "Journal of Pediatric Gastroenterology and Nutrition",
    year: 2018,
    doi: "10.1097/mpg.0000000000001867"
  },
  {
    title: "Ventral intermediate nucleus versus zona incerta region deep brain stimulation in essential tremor.",
    authors: ["Eisinger, R. S.", "Wong, J.", "Almeida, L.", "Ramirez‐Zamora, A.", "Cagle, J. N.", "Giugni, J. C.", "...", "Martinez‐Ramirez, D."],
    journal: "Movement Disorders Clinical Practice",
    year: 2018,
    doi: "10.1093/med/9780190647209.003.0006"
  }
];

const awards = [
  {
    title: "UF Innovate Standing InnOvation Award",
    year: 2022,
  },
  {
    title: "Society for Neuroscience Trainee Professional Development Award",
    year: 2017,
  },
  {
    title: "Herbert Werthein College of Engineering Outstanding Gator Engineer M.S. Scholar",
    year: 2017,
  },
  {
    title: "NSF Award for Young Professionals Contributing to Smart and Connected Health",
    year: 2016,
  },
  {
    title: "Summa cum Laude",
    year: 2016,
  },
  {
    title: "Anderson Scholar Award with High Distinction",
    year: 2015,
  },
  {
    title: "Thomas O. Hunter Leadership Award nomination by the Biomedical Engineering Department",
    year: 2015,
  },
  {
    title: "UF Biomedical Engineering Department Undergraduate Student Award of Excellence",
    year: 2015,
  },
  {
    title: "Biomedical Engineering Society UF Chapter, Director Award, Vice-President Award",
    year: 2014,
  },
  {
    title: "NASA Florida Space Grant Consortium, Finalist in VIII Florida Satellite Design Competition",
    year: 2014,
  },
  {
    title: "Who’s Who Among American Colleges and Universities",
    year: 2013,
  },
  {
    title: "Mu Alpha Theta Distinguished Leadership Award",
    year: 2013,
  },
  {
    title: "Chipola Community College, President’s Award",
    year: 2013,
  },
  {
    title: "Chipola Community College, Summa cum Laude",
    year: 2013,
  },
  {
    title: "Principal’s List and Honor Roll",
    year: 2013,
  },
  {
    title: "Principal’s Medallion for Scholastic Achievement",
    year: 2013,
  },
  {
    title: "Dean’s List",
    year: 2014,
  },
  {
    title: "Dean’s List",
    year: 2012,
  },
  {
    title: "Calculus II Award",
    year: 2012,
  }
];

function AboutMePage() {
  const [collapse, setCollapse] = useState(0);

  useEffect(() => { 
    if (window.__dimensions_embed) {
      window.__dimensions_embed.addBadges();
    }
  }, [window]);

  return (
    <>
      <Navbar />
      <AboutMeHeader />
      <Card
        sx={{
          p: 2, mx: { xs: 2, lg: 3 },
          mt: -8, mb: 4,
          backgroundColor: ({ palette: { white }, functions: { rgba } }) => rgba(white.main, 0.8),
          backdropFilter: "saturate(200%) blur(30px)",
          boxShadow: ({ boxShadows: { xxl } }) => xxl,
        }}
      >
        <MKBox component="section" position="relative" py={2} style={{ top: -100 }}>
          <Container sx={{ maxWidth: "100% !important" }}>
            <Grid container item xs={10} sx={{ mx: "auto" }}>
              <MKBox variant="gradient" bgColor="dark" borderRadius="lg" width="100%" py={6}>
                <Grid container>
                  <Grid item xs={12} md={6} xl={4} sx={{ position: "relative", px: 6 }}>
                    <MKBox
                      component="img"
                      src={JCaglePhoto}
                      alt="image"
                      borderRadius="md"
                      maxWidth="300px"
                      width="100%"
                      position="relative"
                      mt={-12}
                    />
                  </Grid>
                  <Grid
                    item xs={12} xl={4} px={6}
                    sx={{
                      position: "relative", display: "flex",
                      color: ({ palette: { white } }) => white.main,
                    }}
                  >
                    <MKTypography
                      variant="body2"
                      color="white"
                      fontWeight="light"
                      mb={2}
                      sx={{ fontSize: "1.125rem", justifyContent: "start" }}
                    >
                      {"I am currently working as a Research Software Engineer at the Fixel Institute for Neurological Diseases at the University of Florida, \
                      specializing in software development and neural signal data analysis. My works \
                      focus on creating robust computational tools and analytical frameworks that \
                      advance neuroscience research. Driven by a passion for transforming complex neural \
                      signals into meaningful insights, I aim to bridge engineering and science to support \
                      discovery and innovation."}
                    </MKTypography>
                  </Grid>
                  <Grid item xs={1} />
                  <Grid
                    item xs={12} xl={2}
                    sx={{ px: { xs: 6, xl: 0 }, my: { xs: 0, xl: "auto" }, lineHeight: 1 }}
                  >
                    <MKTypography variant="h3" color="white" mt={{ xs: 6, xl: 0 }} mb={1}>
                      Contact
                    </MKTypography>
                    <MKTypography component="p" variant="button" color="white" opacity={0.8} mb={2}>
                      Email: 
                      <MKTypography
                        component="a"
                        href="mailto:jackson.cagle@neurology.ufl.edu"
                        variant="button"
                        color="white"
                        fontWeight="regular"
                      > 
                      {" jackson.cagle@neurology.ufl.edu"}
                      </MKTypography>
                    </MKTypography>
                    
                    <MKTypography component="p" variant="button" color="white" opacity={0.8} mb={2}>
                      College of Medicine | Department of Neurology <br/>
                      Fixel Institute for Neurological Diseases  <br/> Room 1155 <br/>
                      3011 SW Williston Rd, Gainesville, FL 32608 <br/>
                    </MKTypography>
                  </Grid>
                </Grid>
              </MKBox>
            </Grid>
          </Container>
        </MKBox>

        <MKBox component="section" position="relative" py={2} sx={{ backgroundColor: "#f0f0f0ff" }}>
          <Container sx={{ maxWidth: "80% !important" }}>
            <Grid container justifyContent="center">
              <Grid item xs={12}>
                <MKTypography variant="h2" align="start" fontWeight="bold" gutterBottom>
                  Education
                </MKTypography>
              </Grid>
              <Grid item xs={12}>
                <EducationCollapse
                  title="University of Florida - Ph.D. in Biomedical Engineering (Neural Engineering Track)"
                  year="August 2016 - May 2020"
                  open={collapse === 1}
                  onClick={() => (collapse === 1 ? setCollapse(false) : setCollapse(1))}
                >
                  <MKTypography variant="h6" fontWeight="bold">
                    <i>Dissertation: Improved Therapy for Patients with Tourette Syndrome through Closed-loop Deep Brain Stimulation</i>
                  </MKTypography>
                  <MKTypography variant="h6" fontWeight="bold">
                    Advisor: Aysegul Gunduz, Ph.D.
                  </MKTypography>
                </EducationCollapse>
                <EducationCollapse
                  title="University of Florida - B.S. in Biomedical Engineering "
                  year="August 2013 - May 2016"
                  open={collapse === 2}
                  onClick={() => (collapse === 2 ? setCollapse(false) : setCollapse(2))}
                >
                  <MKTypography variant="h6" fontWeight="bold">
                    <i>Thesis: Sources of Delta and Their Functions in a Fatigue Paradigm</i>
                  </MKTypography>
                  <MKTypography variant="h6" fontWeight="bold">
                    Advisor: Mingzhou Ding, Ph.D.
                  </MKTypography>
                </EducationCollapse>
              </Grid>
            </Grid>
          </Container>
        </MKBox>
        
        <MKBox component="section" position="relative" py={2} sx={{ backgroundColor: "#f8f8f8ff" }}>
          <Container sx={{ maxWidth: "80% !important" }}>
            <Grid container justifyContent="center">
              <Grid item xs={12}>
                <MKTypography variant="h2" align="start" fontWeight="bold" gutterBottom>
                  Research Experiences
                </MKTypography>
              </Grid>
              <Grid item xs={12}>
                <ExperienceCard
                  title="Research Software Engineer III"
                  location="Fixel Institute for Neurological Diseases, Gainesville, FL"
                  year="2022 - Present"
                >
                  <MKTypography variant="body2" color="text">
                    {"I lead the design, development, and maintenance of the Brain Recording Analysis and Visualization Online (BRAVO) Platform, \
                    a cloud-based solution for neural data management, signal processing, and visualization. BRAVO enables clinicians and researchers \
                    to analyze electrophysiological and neuroimaging data without requiring custom scripts or high-performance computing resources, making \
                    advanced analytics accessible across devices. My responsibilities include building scalable architectures, implementing automated pipelines \
                    for chronic brain signal analysis, and delivering real-time visualization tools. I also developed the BRAVO Wearable Application, which \
                    leverages Apple’s FDA-cleared Movement Disorder Kit and HealthKit to collect longitudinal symptom data from study participants. In addition \
                    to software development, I contribute to neuromodulation research and translational neuroscience, with publications in high-impact journals. \
                    This role combines expertise in biomedical signal processing, neuroinformatics, and software engineering to create innovative tools that advance \
                    both research and clinical care."}
                  </MKTypography>
                </ExperienceCard>
              </Grid>
              <Grid item xs={12}>
                <ExperienceCard
                  title="Postdoctoral Research Associate"
                  location="Fixel Institute for Neurological Diseases, Gainesville, FL"
                  year="2020 - 2022"
                >
                  <MKTypography variant="body2" color="text">
                    {"I developed data-driven pipelines to enhance clinical programming of deep brain stimulation (DBS) devices for \
                    Parkinson’s disease patients using Medtronic’s sensing-enabled neurostimulator, Percept PC and RC. My work focused \
                    on decoding and interpreting Percept data files collected via clinical tablets and creating signal processing workflows \
                    to extract meaningful biomarkers for therapy optimization. In addition to algorithm development, I contributed to the design \
                    of wearable solutions for tracking chronic symptoms and conducting objective motor assessments during clinical visits. \
                    This role combined expertise in neuroengineering, biomedical signal processing, and translational neuroscience to bridge \
                    advanced computational methods with patient care."}
                  </MKTypography>
                </ExperienceCard>
              </Grid>
              <Grid item xs={12}>
                <ExperienceCard
                  title="Graduate Research Assistant"
                  location="Brain Mapping Laboratory, Gainesville, FL"
                  year="2016 - 2020"
                >
                  <MKTypography variant="body2" color="text">
                    {"As part of NIH R01 and NSF CAREER-funded projects, I investigated the electrophysiology of the human thalamus, primary motor cortex, \
                    and thalamocortical connectivity in patients with Tourette syndrome using research-only sensing-enabled deep brain stimulation (DBS) devices, \
                    the Activa PC+S and RC+S system. My work focused on developing neural signal processing pipelines for time-domain data and creating software \
                    tools that allowed clinicians to visualize electrode localization and signal characteristics. I also designed an intraoperative neurophysiology-based \
                    targeting procedure for accurate identification of the centromedian nucleus of the thalamus. In addition to technical development, I managed patient \
                    recruitment and coordinated monthly clinical visits, contributing to translational research aimed at improving DBS therapy for movement disorders."}
                  </MKTypography>
                </ExperienceCard>
              </Grid>
              <Grid item xs={12}>
                <ExperienceCard
                  title="Undergraduate Research Assistant"
                  location="Neuroinformatics Laboratory, Gainesville, FL"
                  year="2013 - 2016"
                >
                  <MKTypography variant="body2" color="text">
                    {"I contributed to projects investigating mental fatigue and working memory in Parkinson’s disease and aging populations using EEG-based \
                    analysis during cognitive tasks. My responsibilities included preprocessing, signal filtering, and statistical analysis of EEG data, as well \
                    as implementing advanced signal processing techniques such as Independent Component Analysis (ICA), Common Average Reference, and Principal \
                    Component Analysis (PCA). I also worked on source localization methods, including Dynamic Imaging Coherence Source and standardized LORETA, \
                    for deep brain projection. Additionally, I developed a portable brain-computer interface leveraging alpha rhythms to detect visual attention regions. \
                    This role provided foundational experience in neuroinformatics, EEG analysis, and computational neuroscience."}
                  </MKTypography>
                </ExperienceCard>
              </Grid>
              <Grid item xs={12}>
                <ExperienceCard
                  title="Lead Software & Avionics Engineer"
                  location="Space System Design Group, Gainesville, FL"
                  year="2014 - 2020"
                >
                  <MKTypography variant="body2" color="text">
                    {"As part of NASA’s "}
                    <b>Reduced Gravity Education Flight Program</b>
                    {", I developed instrumentation and data acquisition software for testing a stiff deployment mechanism for small satellite platforms. \
                    This included coding for data collection, signal filtering, and storage in the Arduino environment. The experiment was successfully flown \
                    on a reduced-gravity aircraft at Johnson Space Center in 2014. I contributed to multiple NASA-funded small satellite and microgravity research \
                    projects, focusing on communication systems, avionics design, and software development. As part of the SwampSat II mission, I led the design \
                    and implementation of space-to-ground telemetry systems, satellite tracking software, and onboard subsystem communication protocols. My \
                    responsibilities included component selection, electrical system integration, and embedded programming for the onboard computer. I also designed \
                    and implemented a custom-made analog-to-digital converter with a sampling rate of 100,000Hz for sampling very-low-frequency (VLF) waves in the \
                    ionosphere using assembly and C language on a flight computer. I also co-authored an NSF grant proposal and a detailed design report; the initial \
                    design won first place in the VIII Florida University Satellite Design Competition and was later launched in December 2019 under NASA’s "}
                    <b>Undergraduate Student Instrument Program</b>
                    {". "}
                  </MKTypography>
                </ExperienceCard>
              </Grid>
              <Grid item xs={12}>
                <ExperienceCard
                  title="Clinical Diagnostic Method Developer"
                  location="Department of Pediatric, Gainesville, FL"
                  year="2014 - 2016"
                >
                  <MKTypography variant="body2" color="text">
                    {"I collaborated with biomedical engineers from Johns Hopkins and UT Southwestern to develop non-invasive monitoring methods for feeding intolerance \
                    in premature infants. Responsibilities included designing MATLAB-based analysis scripts, performing signal processing on Electrogastrogram (EGG), \
                    Near-Infrared Spectroscopy (NIRS), and bowel acoustic data, and developing algorithms to distinguish feeding tolerance profiles. This work contributed \
                    to the advancement of real-time diagnostic tools for neonatal care."}
                  </MKTypography>
                </ExperienceCard>
              </Grid>
            </Grid>
          </Container>
        </MKBox>

        <MKBox component="section" position="relative" py={2} sx={{ backgroundColor: "#f0f0f0ff" }}>
          <Container sx={{ maxWidth: "80% !important" }}>
            <Grid container justifyContent="center">
              <Grid item xs={12}>
                <MKTypography variant="h2" align="start" fontWeight="bold" gutterBottom>
                  Work Experiences
                </MKTypography>
              </Grid>
              <Grid item xs={12}>
                <ExperienceCard
                  title="Software Development Contractor"
                  location="Precision One Health, Inc., Gainesville, FL"
                  year="2019"
                >
                  <MKTypography variant="body2" color="text">
                    {"During graduate school, I worked as a contract developer for a local startup, building an iOS application designed for patient education and \
                    clinic experience feedback. I developed the app in Swift, incorporating SceneKit for 3D models and animations to help clinicians visually explain \
                    pathologies. Additionally, I developed the backend infrastructure utilizing AWS Storage, MongoDB, and Node.js, which enabled secure data transfer \
                    and automated content updates. This project strengthened my expertise in mobile development and cloud-based solutions for healthcare applications."}
                  </MKTypography>
                </ExperienceCard>
              </Grid>
              <Grid item xs={12}>
                <ExperienceCard
                  title="Course Instructor"
                  location="Student Science Training Program, University of Florida, Gainesville, FL"
                  year="2017 - 2019"
                >
                  <MKTypography variant="body2" color="text">
                    {"I designed and co-taught a 7-week seminar every summer, introducing high school students to neuromodulation, neurological disorders, \
                    and biosensing technologies. The curriculum covered topics such as neural physiology, signal acquisition systems, stimulation methods, \
                    prosthetics, machine learning applications, and brain-computer interfaces. To reinforce learning, I guided student teams through hands-on projects, \
                    including building muscle-activated remote-control cars and LED displays responsive to cardiac activity. This experience emphasized the importance of \
                    interactive teaching and mentoring in neuroscience and biomedical engineering."}
                  </MKTypography>
                </ExperienceCard>
              </Grid>
              <Grid item xs={12}>
                <ExperienceCard
                  title="Neuroscience Engineer Intern"
                  location="Tucker Davis Technology, Alachua, FL"
                  year="2016"
                >
                  <MKTypography variant="body2" color="text">
                    {"I supported software and hardware development for neuroscience research systems. My work included creating PCB footprints in Altium for new \
                    digitizing headstages, developing testing software for the RZ2 BioAmp Processor, and performing hardware diagnostics on audio components. Additionally, \
                    I built a Python API for Synapse Software to streamline integration and automation. This role strengthened my expertise in embedded systems, \
                    hardware testing, and software development for biomedical applications."}
                  </MKTypography>
                </ExperienceCard>
              </Grid>
            </Grid>
          </Container>
        </MKBox>

        <MKBox component="section" position="relative" py={2} sx={{ backgroundColor: "#f8f8f8ff" }}>
          <Container sx={{ maxWidth: "80% !important" }}>
            <Grid container justifyContent="center">
              <Grid item xs={12}>
                <MKTypography variant="h2" align="start" fontWeight="bold" gutterBottom>
                  Publications
                </MKTypography>
              </Grid>
              {publications.map((pub, index) => (
                <Grid item xs={12} key={index} mb={2} sx={{ display: "flex", gap: 2, justifyContent: "flex-start", alignItems: "center" }}>
                  <span className="__dimensions_badge_embed__"
                    data-doi={pub.doi}
                    data-style="small_circle"
                    data-legend="hover-right">
                  </span>
                  <MKBox>
                    <MKTypography variant="h6" fontWeight="bold">
                      {pub.title}
                    </MKTypography>
                    <MKTypography variant="body2" color="text">
                      {pub.authors.map((author, i) => (
                        <span key={i}>
                          {author.includes("Cagle") ? <b>{author}</b> : author}
                          {i < pub.authors.length - 1 ? ", " : ""} 
                        </span>
                      ))} <i>{pub.journal}</i>, {pub.year}.
                    </MKTypography>
                  </MKBox>
                </Grid> 
              ))}
            </Grid>
          </Container>
        </MKBox>

        <MKBox component="section" position="relative" py={2} pb={3} sx={{ backgroundColor: "#f0f0f0ff" }}>
          <Container sx={{ maxWidth: "80% !important" }}>
            <Grid container justifyContent="center">
              <Grid item xs={12}>
                <MKTypography variant="h2" align="start" fontWeight="bold" gutterBottom>
                  Honors and Awards
                </MKTypography>
              </Grid>
              {awards.map((award, index) => (
                <Grid item xs={12} key={index}>
                  <MKBox display="flex" flexDirection="row" justifyContent={"space-between"}>
                    <MKTypography variant="h6" fontWeight="regular">
                      {award.title}
                    </MKTypography>
                    <MKTypography variant="body2" color="text">
                      {award.year}
                    </MKTypography>
                  </MKBox>
                </Grid> 
              ))}
              
            </Grid>
          </Container>
        </MKBox>

      </Card>
      <MKBox pt={6} px={1} mt={6}>
        <Footer />
      </MKBox>
    </>
  );
}

export default AboutMePage;
